/**
 * TUTORIAL OVERLAY v4.3
 * ✅ fix 2: handleNext estabilizado con useRef en el listener wait-for-action
 *           — antes el listener capturaba handleNext del primer render (closure stale)
 *             causando que al completar un paso, el índice quedara en 0 en lugar
 *             de avanzar al paso correcto
 * ✅ Todo lo demás de v4.2 preservado (spotlight, escudo de clics, escotilla, scroll)
 */
import { useState, useEffect, useRef } from 'react';
import {
  SparklesIcon,
  ArrowRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import { useTutorial } from '../context/TutorialContext';
import { useNavigate, useLocation } from 'react-router-dom';

// ═══════════════════════════════════════════════
// PASOS DEL TUTORIAL
// ═══════════════════════════════════════════════
const INTERACTIVE_STEPS = [
  {
    id: 'welcome',
    page: null,
    title: '¡Bienvenido a MealCycle! 👋',
    subtitle: 'En 5 minutos vas a dominar la app',
    description: 'MealCycle te ayuda a planificar comidas y generar listas de compras automáticamente. Es simple, pero tiene un flujo específico.',
    illustration: '🎯',
    color: 'from-blue-500 to-purple-500',
    action: { type: 'continue', label: 'Empezar Tutorial (5 min)' }
  },
  {
    id: 'concept',
    page: null,
    title: 'El Ciclo de MealCycle 🔄',
    subtitle: 'Entendé cómo funciona',
    description: 'Todo sigue este orden:',
    steps: [
      { icon: '🧩', text: 'Componentes', desc: 'Platos individuales (ej: Milanesa, Puré)' },
      { icon: '📖', text: 'Recetas',     desc: 'Combinás componentes (ej: Milanesa + Puré)' },
      { icon: '📅', text: 'Planificador',desc: 'Organizás tu semana' },
      { icon: '🛒', text: 'Lista de Compras', desc: 'Se genera automáticamente' },
      { icon: '📦', text: 'Stock',       desc: 'Se descuenta al cocinar' }
    ],
    illustration: '🔄',
    color: 'from-purple-500 to-pink-500',
    action: { type: 'continue', label: 'Entendido, sigamos' }
  },
  {
    id: 'goto-components',
    page: '/components',
    title: 'Paso 1: Componentes 🧩',
    subtitle: 'Los bloques base de todo',
    description: 'Un componente es un plato individual. Por ejemplo: "Milanesa", "Puré", "Ensalada". Podés cargar ejemplos o crear los tuyos.',
    highlightElement: '#load-examples-btn',
    blockUI: true,
    illustration: '🧩',
    color: 'from-orange-500 to-red-500',
    instruction: '👉 Tocá el botón "Cargar Ejemplos"',
    action: { type: 'wait-for-action', elementId: 'load-examples-btn', event: 'click' }
  },
  {
    id: 'components-loaded',
    page: '/components',
    title: '¡Perfecto! 🎉',
    subtitle: 'Ya tenés componentes listos',
    description: 'Ahora tenés milanesas, purés, ensaladas y mucho más. Estos son los bloques que vas a usar para armar recetas.',
    celebration: true,
    illustration: '✅',
    color: 'from-green-500 to-emerald-500',
    action: { type: 'continue', label: 'Siguiente: Recetas' }
  },
  {
    id: 'goto-recipes',
    page: '/recipes',
    title: 'Paso 2: Recetas 📖',
    subtitle: 'Combiná componentes',
    description: 'Las recetas son combinaciones de componentes. Por ejemplo: "Milanesa con Puré" es una receta que tiene 2 componentes.',
    highlightElement: '#load-recipes-btn',
    blockUI: true,
    illustration: '📖',
    color: 'from-blue-500 to-indigo-500',
    instruction: '👉 Tocá "Cargar Recetas de Ejemplo"',
    action: { type: 'wait-for-action', elementId: 'load-recipes-btn', event: 'click' }
  },
  {
    id: 'recipes-loaded',
    page: '/recipes',
    title: '¡Excelente! 🎊',
    subtitle: 'Ya tenés recetas listas',
    description: 'Ahora tenés platos completos listos para ser planificados en tu semana.',
    celebration: true,
    illustration: '✅',
    color: 'from-green-500 to-emerald-500',
    action: { type: 'continue', label: 'Siguiente: Planificar' }
  },
  {
    id: 'goto-planner',
    page: '/planner',
    title: 'Paso 3: Planificá tu Semana 📅',
    subtitle: 'Organizá qué vas a comer',
    description: 'Asigná las recetas a cada día. La app calculará automáticamente qué ingredientes necesitás comprar.',
    highlightElement: '[data-tutorial="day-expand-btn"]',
    blockUI: true,
    illustration: '📅',
    color: 'from-purple-500 to-pink-500',
    instruction: '👉 Tocá cualquier día para expandirlo',
    action: { type: 'wait-for-action', selector: '[data-tutorial="day-expand-btn"]', event: 'click' }
  },
  {
    id: 'planner-day-expanded',
    page: '/planner',
    title: 'Agregá una comida 🍽️',
    subtitle: 'Elegí un momento del día',
    description: 'Podés planificar desayuno, almuerzo, merienda y cena.',
    highlightElement: '[data-tutorial="add-meal-btn"]',
    blockUI: true,
    illustration: '➕',
    color: 'from-blue-500 to-cyan-500',
    instruction: '👉 Tocá "Agregar comida"',
    action: { type: 'wait-for-action', selector: '[data-tutorial="add-meal-btn"]', event: 'click' }
  },
  {
    id: 'select-recipe',
    page: '/planner',
    title: 'Elegí una receta 🎯',
    subtitle: 'Cualquiera está bien',
    description: 'Elegí cualquier receta. Esto es solo una prueba, después podés cambiarla.',
    highlightElement: '[data-tutorial="recipe-card"]',
    blockUI: true,
    illustration: '👆',
    color: 'from-orange-500 to-red-500',
    instruction: '👉 Tocá cualquier receta',
    action: { type: 'wait-for-action', selector: '[data-tutorial="recipe-card"]', event: 'click' }
  },
  {
    id: 'meal-added',
    page: '/planner',
    title: '¡Genial! 🎉',
    subtitle: 'Primera comida planificada',
    description: 'Así de fácil es organizar tu semana. Ahora la app sabe qué vas a comer.',
    celebration: true,
    illustration: '✅',
    color: 'from-green-500 to-emerald-500',
    action: { type: 'continue', label: 'Siguiente: Lista de Compras' }
  },
  {
    id: 'goto-shopping',
    page: '/shopping',
    title: 'Paso 4: Lista de Compras 🛒',
    subtitle: 'Generación automática',
    description: 'La app va a calcular qué ingredientes necesitás para las comidas que planificaste.',
    highlightElement: '[data-tutorial="generate-from-plan-btn"]',
    blockUI: true,
    illustration: '🛒',
    color: 'from-green-500 to-teal-500',
    instruction: '👉 Tocá "Desde Plan"',
    action: { type: 'wait-for-action', selector: '[data-tutorial="generate-from-plan-btn"]', event: 'click' }
  },
  {
    id: 'select-dates',
    page: '/shopping',
    title: 'Seleccioná las fechas 📅',
    subtitle: 'Por defecto es esta semana',
    description: 'Elegí el rango de días para tu lista de compras.',
    highlightElement: '[data-tutorial="generate-confirm-btn"]',
    blockUI: true,
    illustration: '📅',
    color: 'from-blue-500 to-purple-500',
    instruction: '👉 Tocá "Generar"',
    action: { type: 'wait-for-action', selector: '[data-tutorial="generate-confirm-btn"]', event: 'click' }
  },
  {
    id: 'list-generated',
    page: '/shopping',
    title: '¡Listo! 🎊',
    subtitle: 'Tu lista está generada',
    description: 'La app calculó qué te falta comprar comparando las recetas con tu stock.',
    celebration: true,
    illustration: '✅',
    color: 'from-green-500 to-emerald-500',
    tip: '💡 Tip: Podés editar cantidades, agregar items manuales, y guardar precios.',
    action: { type: 'continue', label: 'Último paso' }
  },
  {
    id: 'cooking-explained',
    page: null,
    title: 'Cocinar y Descontar Stock 🔥',
    subtitle: 'Esto es clave',
    description: 'Cuando tocás "Cocinar" en el planificador, la app descuenta automáticamente los ingredientes del stock.',
    illustration: '🔥',
    color: 'from-orange-500 to-red-500',
    tip: '⚠️ Importante: Siempre marcá como cocinado cuando prepares la comida.',
    action: { type: 'continue', label: 'Siguiente' }
  },
  {
    id: 'complete',
    page: null,
    title: '¡Tutorial Completo! 🎉',
    subtitle: 'Ya sabés usar MealCycle',
    description: 'Recordá el flujo: Componentes → Recetas → Planificar → Compras → Stock. ¡A disfrutar!',
    celebration: true,
    illustration: '🎊',
    color: 'from-green-500 to-emerald-500',
    action: { type: 'finish', label: '¡Empezar a usar MealCycle!' }
  }
];

// ═══════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════
export default function TutorialOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showOnboarding, setShowOnboarding, completeOnboarding, completeStep } = useTutorial();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState(null);
  const [targetRect, setTargetRect]   = useState(null);
  const [isWaitingForAction, setIsWaitingForAction] = useState(false);
  const [cardPosition, setCardPosition] = useState('center');

  const currentStep = INTERACTIVE_STEPS[currentStepIndex];

  // fix 2: ref siempre actualizado → el listener nunca tiene closure stale
  const handleNextRef = useRef(null);

  const handleFinish = () => {
    completeOnboarding();
    setShowOnboarding(false);
    navigate('/planner');
  };

  const handleNext = () => {
    completeStep(currentStep.id);
    if (currentStepIndex < INTERACTIVE_STEPS.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  // Mantener la ref sincronizada con la última versión de handleNext
  handleNextRef.current = handleNext;

  // 1. Reset al iniciar
  useEffect(() => {
    if (showOnboarding) setCurrentStepIndex(0);
  }, [showOnboarding]);

  // 2. Navegación automática
  useEffect(() => {
    if (!currentStep || !showOnboarding) return;
    if (currentStep.page && location.pathname !== currentStep.page) {
      navigate(currentStep.page);
    }
  }, [currentStep, location.pathname, navigate, showOnboarding]);

  // 3. Buscador del elemento + auto-scroll
  useEffect(() => {
    if (!currentStep || !showOnboarding) return;
    let hasScrolled = false;
    const findElement = () => {
      if (currentStep.highlightElement) {
        const el = document.querySelector(currentStep.highlightElement);
        if (el) {
          setHighlightedElement(el);
          if (!hasScrolled) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            hasScrolled = true;
          }
        }
      } else {
        setHighlightedElement(null);
      }
    };
    findElement();
    const interval = setInterval(findElement, 500);
    return () => clearInterval(interval);
  }, [currentStep, showOnboarding]);

  // 4. Tracking en tiempo real del rect del elemento resaltado
  useEffect(() => {
    if (!highlightedElement || !showOnboarding) {
      setTargetRect(null);
      setCardPosition('center');
      return;
    }
    let frameId;
    const updateRect = () => {
      const rect = highlightedElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      setCardPosition(rect.top > viewportHeight / 2 ? 'top' : 'bottom');
      setTargetRect(prev => {
        if (
          prev &&
          Math.abs(prev.top - rect.top) < 1 &&
          Math.abs(prev.left - rect.left) < 1 &&
          prev.width === rect.width
        ) return prev;
        return { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right, width: rect.width, height: rect.height };
      });
      frameId = requestAnimationFrame(updateRect);
    };
    updateRect();
    return () => cancelAnimationFrame(frameId);
  }, [highlightedElement, showOnboarding]);

  // 5. Escudo de clics
  useEffect(() => {
    if (!currentStep?.blockUI || !showOnboarding) return;
    const interceptClick = (e) => {
      if (e.target.closest('#tutorial-card-container')) return;
      if (highlightedElement && (highlightedElement.contains(e.target) || e.target === highlightedElement)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('click', interceptClick, true);
    document.addEventListener('pointerdown', interceptClick, true);
    return () => {
      document.removeEventListener('click', interceptClick, true);
      document.removeEventListener('pointerdown', interceptClick, true);
    };
  }, [currentStep, showOnboarding, highlightedElement]);

  // 6. fix 2: listener usa handleNextRef.current en lugar de handleNext directo
  //    — así siempre ejecuta la versión más reciente con el índice correcto
  useEffect(() => {
    if (!currentStep || !showOnboarding) return;
    if (currentStep.action?.type !== 'wait-for-action') {
      setIsWaitingForAction(false);
      return;
    }
    setIsWaitingForAction(true);
    const selector = currentStep.action.selector || `#${currentStep.action.elementId}`;
    const handleAction = (e) => {
      const target = e.target.closest(selector);
      if (target) {
        setIsWaitingForAction(false);
        setTimeout(() => handleNextRef.current(), 800); // ← ref, no closure
      }
    };
    document.addEventListener(currentStep.action.event || 'click', handleAction, true);
    return () => document.removeEventListener(currentStep.action.event || 'click', handleAction, true);
  }, [currentStep, showOnboarding]); // ← handleNext NO va en deps, usamos ref

  if (!showOnboarding || !currentStep) return null;

  const handleSkip = () => {
    if (window.confirm('¿Seguro que querés salir del tutorial?')) {
      completeOnboarding();
      setShowOnboarding(false);
    }
  };

  const progress = ((currentStepIndex + 1) / INTERACTIVE_STEPS.length) * 100;

  const flexPositionClass =
    cardPosition === 'top'    ? 'items-start pt-16' :
    cardPosition === 'center' ? 'items-center'      :
                                'items-end pb-8';

  const showMainActionButton =
    currentStep.action?.type === 'continue' ||
    currentStep.action?.type === 'finish';

  return (
    <>
      {/* Fondo oscuro cuando no hay elemento resaltado */}
      {currentStep.blockUI && !targetRect && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] pointer-events-none" />
      )}

      {/* Spotlight sobre el elemento resaltado */}
      {targetRect && currentStep.blockUI && (
        <div
          className="fixed rounded-[16px] pointer-events-none"
          style={{
            zIndex: 100,
            top:    targetRect.top  - 8,
            left:   targetRect.left - 8,
            width:  targetRect.width  + 16,
            height: targetRect.height + 16,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.85), 0 0 0 4px rgba(59,130,246,0.5), 0 0 20px rgba(59,130,246,0.6)',
            willChange: 'top, left, width, height',
            animation: 'pulse-spotlight 2s infinite'
          }}
        />
      )}

      {/* Card del tutorial */}
      <div
        id="tutorial-card-container"
        className={`fixed inset-0 z-[110] flex sm:items-center justify-center p-4 pointer-events-none transition-all duration-300 ${flexPositionClass}`}
      >
        <div className="w-full max-w-lg pointer-events-auto flex flex-col max-h-[85vh]">

          {/* Barra de progreso */}
          <div className="mb-3 flex-shrink-0">
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 px-1">
              <span className="text-xs text-gray-400">
                Paso {currentStepIndex + 1} de {INTERACTIVE_STEPS.length}
              </span>
              <button onClick={handleSkip} className="text-xs text-gray-500 hover:text-gray-300 transition">
                Cerrar tutorial
              </button>
            </div>
          </div>

          {/* Card principal */}
          <div className="bg-gray-900 border-2 border-gray-700 rounded-2xl shadow-2xl animate-slideUp flex flex-col min-h-0">

            {/* Header */}
            <div className={`relative bg-gradient-to-br ${currentStep.color} p-6 sm:p-8 text-center flex-shrink-0 rounded-t-xl`}>
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                  backgroundSize: '24px 24px'
                }} />
              </div>
              <div className="relative text-5xl sm:text-7xl mb-2 sm:mb-4 animate-bounce-slow">
                {currentStep.illustration}
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2">
                {currentStep.title}
              </h2>
              {currentStep.subtitle && (
                <p className="text-white/80 text-xs sm:text-sm font-medium">{currentStep.subtitle}</p>
              )}
              {currentStep.celebration && (
                <div className="absolute top-0 left-0 right-0 flex justify-center gap-4 animate-confetti pointer-events-none">
                  <span className="text-4xl">🎉</span>
                  <span className="text-4xl">🎊</span>
                  <span className="text-4xl">✨</span>
                </div>
              )}
            </div>

            {/* Contenido con scroll interno */}
            <div className="p-5 sm:p-6 overflow-y-auto custom-scrollbar flex-1">
              <p className="text-gray-300 text-sm leading-relaxed mb-4">
                {currentStep.description}
              </p>

              {currentStep.steps && (
                <div className="space-y-2 mb-4">
                  {currentStep.steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-gray-800/50 p-3 rounded-lg border border-gray-700/50">
                      {step.icon && <span className="text-xl sm:text-2xl">{step.icon}</span>}
                      {step.num && (
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {step.num}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{step.text}</p>
                        {step.desc && <p className="text-gray-400 text-xs mt-0.5">{step.desc}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {currentStep.instruction && (
                <div className="bg-blue-600/10 border border-blue-500/30 rounded-xl p-3 sm:p-4 mb-4">
                  <p className="text-blue-400 text-sm font-bold text-center">{currentStep.instruction}</p>
                </div>
              )}

              {currentStep.tip && (
                <div className="bg-yellow-600/10 border border-yellow-500/30 rounded-xl p-3 mb-4">
                  <p className="text-yellow-400 text-xs">{currentStep.tip}</p>
                </div>
              )}

              {/* Botón principal */}
              {showMainActionButton && (
                <button
                  onClick={currentStep.action?.type === 'finish' ? handleFinish : handleNext}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white py-3 sm:py-4 rounded-xl font-bold transition-all duration-200 active:scale-95 flex items-center justify-center gap-2 shadow-lg mt-2"
                >
                  {currentStep.action?.type === 'finish' ? (
                    <><CheckCircleIcon className="w-5 h-5" />{currentStep.action.label}</>
                  ) : (
                    <>{currentStep.action.label}<ArrowRightIcon className="w-5 h-5" /></>
                  )}
                </button>
              )}

              {/* Escotilla de escape */}
              {isWaitingForAction && (
                <div className="flex flex-col items-center mt-4 gap-4">
                  <div className="inline-flex items-center gap-2 text-blue-400 text-sm font-bold bg-blue-900/20 px-4 py-2 rounded-full border border-blue-500/30">
                    <div className="w-2.5 h-2.5 bg-blue-400 rounded-full animate-ping" />
                    Esperando tu acción...
                  </div>
                  <button
                    onClick={handleNext}
                    className="text-xs text-gray-500 underline hover:text-gray-300 transition-colors"
                  >
                    ¿Ya lo tenés configurado? Avanzar paso
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse-spotlight {
          0%, 100% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.85), 0 0 0 4px rgba(59,130,246,0.5), 0 0 20px rgba(59,130,246,0.5); }
          50%       { box-shadow: 0 0 0 9999px rgba(0,0,0,0.85), 0 0 0 8px rgba(59,130,246,0.8), 0 0 40px rgba(59,130,246,0.9); }
        }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #374151; border-radius: 3px; }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
        @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .animate-bounce-slow { animation: bounce-slow 3s infinite; }
        @keyframes confetti {
          0%   { transform: translateY(-100%) rotate(0deg); opacity: 0; }
          50%  { opacity: 1; }
          100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
        }
        .animate-confetti { animation: confetti 3s ease-out; }
      `}</style>
    </>
  );
}